import React, { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { CloudOff, RefreshCw, X, Loader2 } from "lucide-react";
import { toast } from "sonner";

const POLL_MS = 5 * 60 * 1000; // re-check every 5 minutes

/**
 * Global admin warning shown when locally-written files haven't been
 * mirrored to object storage yet — republishing the site now would lose
 * them (container disk is wiped on every redeploy).
 */
export default function UnsyncedFilesBanner() {
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const [count, setCount] = useState(0);
  const [syncing, setSyncing] = useState(false);
  // Dismiss hides the banner for the current count only — if MORE files
  // become unsynced later, it reappears.
  const [dismissedFor, setDismissedFor] = useState(null);

  const check = useCallback(async () => {
    try {
      const { data } = await api.get("/security/file-sync/status");
      setCount(data?.unsynced_count || 0);
    } catch { /* silent — banner is best-effort */ }
  }, []);

  useEffect(() => {
    if (!isAdmin) return undefined;
    check();
    const t = setInterval(check, POLL_MS);
    return () => clearInterval(t);
  }, [isAdmin, check]);

  const syncNow = async () => {
    setSyncing(true);
    try {
      await api.post("/security/file-sync/run");
      toast.success("File sync complete — all files backed up");
      await check();
      setDismissedFor(null);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Sync failed — object storage unavailable");
    } finally { setSyncing(false); }
  };

  if (!isAdmin || count === 0 || dismissedFor === count) return null;

  return (
    <div
      className="bg-amber-500 text-zinc-900 px-4 py-2 flex items-center justify-between gap-3 border-b-2 border-amber-600 print:hidden"
      data-testid="unsynced-files-banner">
      <div className="flex items-center gap-2.5 min-w-0">
        <CloudOff className="h-4 w-4 flex-shrink-0" />
        <div className="text-xs sm:text-sm font-display font-bold tracking-tight truncate">
          {count} file{count === 1 ? "" : "s"} not yet backed up to object storage
        </div>
        <span className="hidden md:inline text-[10px] uppercase tracking-[0.15em] text-amber-900/80 font-semibold">
          Republishing now could lose {count === 1 ? "it" : "them"} — sync first
        </span>
      </div>
      <div className="flex items-center gap-2 flex-shrink-0">
        <Button size="sm" variant="outline" onClick={syncNow} disabled={syncing}
          className="rounded-sm h-7 px-2 bg-white text-zinc-900 border-amber-700/30 hover:bg-amber-50 text-[11px]"
          data-testid="unsynced-banner-sync-btn">
          {syncing ? <Loader2 className="h-3 w-3 mr-1 animate-spin" /> : <RefreshCw className="h-3 w-3 mr-1" />}
          {syncing ? "Syncing…" : "Sync now"}
        </Button>
        <Link to="/security-stats"
          className="hidden sm:inline-flex items-center text-[10px] uppercase tracking-[0.15em] font-bold text-amber-900 hover:text-zinc-900"
          data-testid="unsynced-banner-details-link">
          Details
        </Link>
        <button type="button" onClick={() => setDismissedFor(count)}
          className="p-1 rounded-sm hover:bg-amber-400"
          data-testid="unsynced-banner-dismiss" aria-label="Dismiss">
          <X className="h-3.5 w-3.5" />
        </button>
      </div>
    </div>
  );
}
